import {
  IGeoJson2DGeographicCoordinates,
  SitePermitStatus,
} from '~/client/graph'
import { FALLBACK_WORKFLOW_ICON_NAME } from '~/client/src/shared/constants/workflowTypeGlyphs'
import Workflow from '~/client/src/shared/models/Workflow'
import { hasPreciseCoordinates } from '~/client/src/shared/utils/locationSnapshot'

import {
  getWorkflowPinColors,
  getWorkflowPinImageKey,
} from '../SitemapHelpers/utils/GLWorkflowPinImageGenerator'
import { pinCoordKey } from './pinCoordKey'

export interface IWorkflowPinPoint {
  coordKey: string
  latitude: number
  longitude: number
  count: number
  iconName: string
  isSelected: boolean
  isDone: boolean
  isAssigned: boolean
  // The group's status, when its members agree. Absent with isMixed set when they do not.
  status?: SitePermitStatus
  isLate?: boolean
  isMixed?: boolean
  // Delivery pins keep their own palette; this routes them to it.
  isDelivery?: boolean
  // Workflow pins never render a cancelled state; delivery pins do.
  isCanceled?: boolean
  // Shared in from another project: greys the pin, and nothing local carries its status.
  isShared?: boolean
  // True when the pin rides a displayed location marker — the layer lifts these
  // clear of it; catch-all pins have no marker beneath, so they don't lift.
  anchored: boolean
  anchorItemId?: string
}

export interface IWorkflowPinCoordOverride {
  coord: IGeoJson2DGeographicCoordinates
  // The displayed globe item the coord belongs to (absent for the project
  // catch-all, whose coord is the location's own even though it isn't shown).
  anchorItemId?: string
}

export interface IWorkflowPinPointsOptions {
  // Workflows without a precise location anchor at their nearest displayed
  // ancestor, or the project catch-all when nothing in the chain is displayed.
  coordOverrides?: Map<string, IWorkflowPinCoordOverride>
  getIconName: (workflow: Workflow) => string
  isAssignedToUser: (workflow: Workflow) => boolean
  // The lift only makes sense over a drawn circle.
  isAnchorCircleShown: (anchorItemId: string) => boolean
  isPinSelected: (
    workflowsAtLocation: Workflow[],
    anchorItemId?: string,
  ) => boolean
}

export interface ICoordGroupEntry {
  latitude: number
  longitude: number
  anchorItemId?: string
}

/** A workflow's own placed pin, if it has one. The pin pipeline reads the FIRST location only. */
export function ownWorkflowPinCoord(
  workflow: Workflow,
): IGeoJson2DGeographicCoordinates | null {
  const precise = workflow?.locations?.[0]?.preciseLocation
  // The point only: a precise location also stores the pose it was placed at, which a workflow
  // sharing its coordinate with others could not honour anyway.
  return hasPreciseCoordinates(precise)
    ? { latitude: precise.latitude, longitude: precise.longitude }
    : null
}

// A pin needs a coordinate: the Workflow's own precise one, or the anchor it rides.
export function filterPinnedWorkflows(
  workflows: Workflow[],
  coordOverrides: Map<string, IWorkflowPinCoordOverride>,
): Workflow[] {
  return workflows.filter(
    p => !!ownWorkflowPinCoord(p) || coordOverrides.has(p.id),
  )
}

// Group pins by exact coordinate. Shared by the pin points (count bubbles) and the labels
// (singleton-only) so both agree on what "one workflow at a location" means. A precise pin
// bit-equal to an anchor coord joins that group first-seen-wins — improbable, harmless.
export function groupWorkflowsByCoord(
  workflows: Workflow[],
  coordOverrides?: Map<string, IWorkflowPinCoordOverride>,
): {
  workflowsByCoordKey: Map<string, Workflow[]>
  coordByKey: Map<string, ICoordGroupEntry>
} {
  const workflowsByCoordKey = new Map<string, Workflow[]>()
  const coordByKey = new Map<string, ICoordGroupEntry>()
  for (const workflow of workflows) {
    const override = coordOverrides?.get(workflow.id)
    const coord = override?.coord ?? ownWorkflowPinCoord(workflow)
    if (coord?.latitude == null || coord?.longitude == null) continue
    const key = pinCoordKey(coord)
    const group = workflowsByCoordKey.get(key)
    if (group) {
      group.push(workflow)
    } else {
      workflowsByCoordKey.set(key, [workflow])
      coordByKey.set(key, {
        latitude: coord.latitude,
        longitude: coord.longitude,
        anchorItemId: override?.anchorItemId,
      })
    }
  }
  return { workflowsByCoordKey, coordByKey }
}

// One point per exact coordinate (2+ workflows there collapse into a count bubble);
// also returns the reverse map for the click handler's pin→workflows lookup.
// Exported for the konva whiteboard pins, which group by shared location position rather than
// by coordinate: one colour and one glyph speak for a group only while the group agrees.
export function iconNameForGroup<T>(
  group: T[],
  getIconName: (item: T) => string,
): string {
  const first = getIconName(group[0])
  return group.every(item => getIconName(item) === first)
    ? first
    : FALLBACK_WORKFLOW_ICON_NAME
}

// One colour has to speak for the whole group, so it only speaks when the group agrees:
// a cluster of differing statuses renders black, which is the only place black is used.
// Exported because the konva whiteboard pins group by shared location position rather than by
// coordinate, and two definitions of "mixed" is two answers to the same question.
export function statusForGroup(group: Workflow[]): {
  status?: SitePermitStatus
  isMixed?: boolean
} {
  const first = group[0].status
  return group.every(workflow => workflow.status === first)
    ? { status: first }
    : { isMixed: true }
}

export function buildWorkflowPinPoints(
  workflows: Workflow[],
  options: IWorkflowPinPointsOptions,
): {
  points: IWorkflowPinPoint[]
  workflowsByCoordKey: Map<string, Workflow[]>
} {
  const { workflowsByCoordKey, coordByKey } = groupWorkflowsByCoord(
    workflows,
    options.coordOverrides,
  )

  const points: IWorkflowPinPoint[] = []
  for (const [coordKey, group] of workflowsByCoordKey) {
    const coord = coordByKey.get(coordKey)
    points.push({
      coordKey,
      latitude: coord.latitude,
      longitude: coord.longitude,
      count: group.length,
      // A cluster can hold several workflow types, and the pin carries one glyph, so a mixed
      // group stays on the neutral badge rather than advertising whichever member sorted first.
      iconName: iconNameForGroup(group, options.getIconName),
      isSelected: options.isPinSelected(group, coord.anchorItemId),
      isDone: group.every(p => p.isDoneOrDenied),
      isAssigned: group.some(p => options.isAssignedToUser(p)),
      ...statusForGroup(group),
      // Any late member makes the pin late, matching how isAssigned reads the group.
      isLate: group.some(p => p.isLate),
      anchored:
        coord.anchorItemId != null &&
        options.isAnchorCircleShown(coord.anchorItemId),
      anchorItemId: coord.anchorItemId,
    })
  }

  return { points, workflowsByCoordKey }
}

// Each property routes the feature to one of the four collision-variant layers
// (single/counted × selected/declutter) — kept pure so routing stays unit-testable.
export function buildWorkflowPinFeatures(
  points: IWorkflowPinPoint[],
  hoveredCoordKey: string | null,
): GeoJSON.Feature[] {
  return points.map(p => {
    const isHovered = p.coordKey === hoveredCoordKey
    const state = {
      iconName: p.iconName,
      withCount: p.count > 1,
      isSelected: p.isSelected,
      isDone: p.isDone,
      isAssigned: p.isAssigned,
      isCanceled: !!p.isCanceled,
      isHovered,
      isShared: !!p.isShared,
      status: p.status,
      isLate: !!p.isLate,
      isMixed: !!p.isMixed,
      isDelivery: !!p.isDelivery,
    }
    return {
      type: 'Feature' as const,
      properties: {
        coordKey: p.coordKey,
        image: getWorkflowPinImageKey(state),
        count: p.count,
        countLabel: p.count > 1 ? String(p.count) : '',
        countColor: getWorkflowPinColors(state).icon,
        hovered: isHovered,
        selected: p.isSelected,
        anchored: p.anchored,
        // Placement priority (lower places first): the hovered pin's 42px image
        // must not lose its slot mid-hover.
        sortKey: isHovered ? 0 : 1,
      },
      geometry: {
        type: 'Point' as const,
        coordinates: [p.longitude, p.latitude],
      },
    }
  })
}
